import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    LinearProgress,
    MenuItem,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCopy, faPlus, faXmark } from '@fortawesome/pro-solid-svg-icons';
import { useSnackbar } from 'notistack';
import { useApplicationContext } from 'ApplicationContext';
import { CreateShareLinkRequest, ShareLinkMode } from 'apiClient/Api';

// Serverseitig untypisiert (any) – defensiv nachgebildet (UI-Spec §4.3).
interface ShareLink {
    id?: string;
    mode?: ShareLinkMode | string;
    customerEmail?: string;
    revoked?: boolean;
    expiresAt?: string | null;
    createdAt?: string;
    createdBy?: string | null;
    lastAccessedAt?: string | null;
    accessCount?: number;
}
interface CreatedShareLink {
    link?: ShareLink;
    url?: string;
    token?: string;
    password?: string;
    emailSent?: boolean;
}

const dateShort = (v?: string | null) => (v ? new Date(v).toLocaleDateString('de-DE') : '');

/** Konfigurator-Link auf dem aktuellen Host (dev/prod), sonst Fallback auf die Server-URL. */
const configuratorUrl = (created: CreatedShareLink): string | undefined =>
    created.token ? `${window.location.origin}/konfigurator/${created.token}` : created.url;

const copyText = async (text: string) => {
    try {
        if (navigator.clipboard?.writeText) {
            await navigator.clipboard.writeText(text);
            return;
        }
    } catch {
        /* ignore */
    }
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    try {
        document.execCommand('copy');
    } catch {
        /* ignore */
    }
    document.body.removeChild(ta);
};

export const LinksDialog: React.FC<{ projectId: string | null; projectName?: string; onClose: () => void }> = ({
    projectId,
    projectName,
    onClose,
}) => {
    const { api, apiError } = useApplicationContext();
    const { enqueueSnackbar } = useSnackbar();
    const [links, setLinks] = useState<ShareLink[]>([]);
    const [loading, setLoading] = useState(false);
    const [form, setForm] = useState<CreateShareLinkRequest>({ mode: ShareLinkMode.Edit, customerEmail: '' });
    const [created, setCreated] = useState<CreatedShareLink | null>(null);

    const reload = useCallback(() => {
        if (!projectId) return;
        setLoading(true);
        api.calculator
            .shareLinkList(projectId)
            .then((r) => setLinks((r.data as unknown as ShareLink[]) ?? []))
            .catch(apiError)
            .finally(() => setLoading(false));
    }, [api, projectId, apiError]);

    useEffect(() => {
        if (projectId) {
            reload();
            setCreated(null);
            setForm({ mode: ShareLinkMode.Edit, customerEmail: '' });
        }
    }, [projectId, reload]);

    const create = async () => {
        if (!projectId || !form.customerEmail?.trim()) return;
        try {
            const r = await api.calculator.shareLinkCreate(projectId, { ...form, customerEmail: form.customerEmail.trim() });
            const c = r.data as unknown as CreatedShareLink;
            setCreated(c);
            setForm({ mode: ShareLinkMode.Edit, customerEmail: '' });
            enqueueSnackbar(
                c.emailSent ? 'Link wurde an den Kunden gesendet.' : 'Link erstellt (E-Mail-Versand fehlgeschlagen).',
                { variant: c.emailSent ? 'success' : 'warning' },
            );
            reload();
        } catch (e) {
            apiError(e);
        }
    };

    const revoke = async (linkId: string) => {
        if (!projectId) return;
        if (!window.confirm('Diesen Link widerrufen? Der Kunde kann ihn danach nicht mehr nutzen.')) return;
        try {
            await api.calculator.shareLinkRevoke(projectId, linkId);
            reload();
        } catch (e) {
            apiError(e);
        }
    };

    return (
        <Dialog open={!!projectId} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>Konfigurator-Links{projectName ? `: ${projectName}` : ''}</DialogTitle>
            <DialogContent dividers>
                {/* Erstellen */}
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap', mb: 2 }} useFlexGap>
                    <TextField
                        size="small"
                        label="Kunden-E-Mail"
                        type="email"
                        value={form.customerEmail}
                        onChange={(e) => setForm({ ...form, customerEmail: e.target.value })}
                        sx={{ minWidth: 240 }}
                    />
                    <TextField
                        select
                        size="small"
                        label="Modus"
                        value={form.mode}
                        onChange={(e) => setForm({ ...form, mode: e.target.value as ShareLinkMode })}
                        sx={{ width: 160 }}
                    >
                        <MenuItem value={ShareLinkMode.Edit}>Bearbeiten</MenuItem>
                        <MenuItem value={ShareLinkMode.View}>Nur ansehen</MenuItem>
                    </TextField>
                    <TextField
                        size="small"
                        label="Ablauf (optional)"
                        type="date"
                        value={form.expiresAt ? form.expiresAt.slice(0, 10) : ''}
                        onChange={(e) => setForm({ ...form, expiresAt: e.target.value ? new Date(e.target.value).toISOString() : null })}
                        slotProps={{ inputLabel: { shrink: true } }}
                    />
                    <Button
                        variant="contained"
                        startIcon={<FontAwesomeIcon icon={faPlus} />}
                        onClick={create}
                        disabled={!form.customerEmail?.trim()}
                    >
                        Link erstellen
                    </Button>
                </Stack>

                {/* Einmalige Geheimnisse nach dem Erstellen */}
                {created && (
                    <Alert severity="success" sx={{ mb: 2 }} onClose={() => setCreated(null)}>
                        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                            Link an {created.link?.customerEmail} {created.emailSent ? 'gesendet' : '(Versand fehlgeschlagen)'} — diese
                            Zugangsdaten werden nur <b>einmal</b> angezeigt:
                        </Typography>
                        <Stack spacing={0.5}>
                            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                                <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                                    {configuratorUrl(created)}
                                </Typography>
                                <IconButton
                                    size="small"
                                    onClick={() => {
                                        const u = configuratorUrl(created);
                                        if (u) copyText(u);
                                    }}
                                >
                                    <FontAwesomeIcon icon={faCopy} />
                                </IconButton>
                            </Stack>
                            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                                <Typography variant="body2">
                                    Passwort: <b style={{ fontFamily: 'monospace' }}>{created.password}</b>
                                </Typography>
                                <IconButton size="small" onClick={() => created.password && copyText(created.password)}>
                                    <FontAwesomeIcon icon={faCopy} />
                                </IconButton>
                            </Stack>
                        </Stack>
                    </Alert>
                )}

                {loading && <LinearProgress sx={{ mb: 1 }} />}

                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Empfänger</TableCell>
                            <TableCell>Modus</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell align="right">Zugriffe</TableCell>
                            <TableCell>Ablauf</TableCell>
                            <TableCell />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {links.map((l) => (
                            <TableRow key={l.id}>
                                <TableCell>{l.customerEmail}</TableCell>
                                <TableCell>{l.mode === ShareLinkMode.View ? 'Ansehen' : 'Bearbeiten'}</TableCell>
                                <TableCell>
                                    {l.revoked ? (
                                        <Chip size="small" label="widerrufen" />
                                    ) : (
                                        <Chip size="small" color="success" label="aktiv" />
                                    )}
                                </TableCell>
                                <TableCell align="right">{l.accessCount ?? 0}×</TableCell>
                                <TableCell>{dateShort(l.expiresAt) || '–'}</TableCell>
                                <TableCell align="right">
                                    {!l.revoked && (
                                        <Tooltip title="Widerrufen">
                                            <IconButton size="small" color="error" onClick={() => l.id && revoke(l.id)}>
                                                <FontAwesomeIcon icon={faXmark} />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                        {links.length === 0 && !loading && (
                            <TableRow>
                                <TableCell colSpan={6}>
                                    <Typography variant="body2" color="text.secondary">
                                        Noch keine Links erstellt.
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Schließen</Button>
            </DialogActions>
        </Dialog>
    );
};
